import * as React from "react";
import { useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Phone, Package } from "lucide-react";
import { formatDate } from "@/lib/utils";

type LineItem = {
  name: string;
  quantity: number;
  total: string;
  image: string;
};

type PublicOrder = {
  shopName: string;
  shopLogoUrl: string;
  shopPrimaryColor: string;
  orderNumber: string;
  status: string;
  createdAt: string;
  currency: string;
  lineItems: LineItem[];
  shippingTotal: string;
  total: string;
  shippingAddress: {
    firstName: string;
    lastName: string;
    address1: string;
    address2: string;
    city: string;
    postcode: string;
    country: string;
    phone: string;
  };
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Na čekanju",
  processing: "U obradi",
  "on-hold": "Pauzirano",
  completed: "Završeno",
  cancelled: "Otkazano",
  refunded: "Refundirano",
  failed: "Neuspješno",
};

function formatStatus(status: string) {
  return STATUS_LABELS[status] ?? status;
}

function displayCurrency(code: string) {
  return code.toUpperCase() === "BAM" ? "KM" : code;
}

function formatMoney(value: string, currency: string) {
  const symbol = displayCurrency(currency);
  const n = Number(value);
  if (!Number.isFinite(n)) return `${value} ${symbol}`;
  return `${n.toLocaleString("bs-BA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`;
}

export function OrderSharePage() {
  const { token } = useParams({ from: "/s/$token" });

  const { data, isLoading, isError } = useQuery<PublicOrder>({
    queryKey: ["public-order", token],
    queryFn: async () => {
      const res = await fetch(`/api/public/orders/${token}`);
      if (!res.ok) throw new Error("invalid");
      return res.json();
    },
    retry: false,
  });

  const accent = data?.shopPrimaryColor || "#b54a2b";
  const pageStyle = { ["--accent" as any]: accent } as React.CSSProperties;

  return (
    <div className="receipt-page" style={pageStyle}>
      <style>{styles}</style>

      <div className="receipt-shell">
        {isLoading && (
          <div className="state-block">
            <div className="state-pulse" />
            <p>Učitavanje narudžbe…</p>
          </div>
        )}

        {(isError || (!isLoading && !data)) && (
          <div className="state-block error">
            <div className="state-stamp">×</div>
            <h1>Link je istekao</h1>
            <p>Ili nije validan. Kontaktirajte trgovinu za novi link.</p>
          </div>
        )}

        {data && (
          <article className="receipt">
            <header className="receipt-head">
              {data.shopLogoUrl ? (
                <img
                  className="brand-logo"
                  src={data.shopLogoUrl}
                  alt={data.shopName}
                />
              ) : (
                <div className="brand">{(data.shopName || "Trgovina").toUpperCase()}</div>
              )}
              <div className="brand-sub">— pregled narudžbe —</div>
            </header>

            <div className="dashed" />

            <section className="meta">
              <div className="meta-row">
                <span className="meta-label">Račun</span>
                <span className="meta-num">№ {data.orderNumber}</span>
              </div>
              <div className="meta-row">
                <span className="meta-label">Datum</span>
                <span className="meta-val">{formatDate(data.createdAt)}</span>
              </div>
              <div className="meta-stamp-wrap">
                <div className="meta-stamp">{formatStatus(data.status)}</div>
              </div>
            </section>

            <div className="dashed" />

            <section className="items">
              <div className="section-label">
                <span>Stavke</span>
                <span className="section-count">{data.lineItems.length}</span>
              </div>

              <ul className="item-list">
                {data.lineItems.map((li, i) => (
                  <li key={i} className="item">
                    <div className="thumb">
                      {li.image ? (
                        <img src={li.image} alt="" loading="lazy" />
                      ) : (
                        <Package className="thumb-fallback" strokeWidth={1.25} />
                      )}
                    </div>
                    <div className="item-body">
                      <div className="item-name">{li.name}</div>
                      <div className="item-qty">
                        <span className="qty-x">×</span>
                        <span className="qty-n">{li.quantity}</span>
                      </div>
                    </div>
                    <div className="item-total">{formatMoney(li.total, data.currency)}</div>
                  </li>
                ))}
              </ul>
            </section>

            <div className="dashed" />

            <section className="totals">
              <div className="total-row">
                <span>Dostava</span>
                <span className="num">{formatMoney(data.shippingTotal, data.currency)}</span>
              </div>
              <div className="total-row final">
                <span>Ukupno</span>
                <span className="num">{formatMoney(data.total, data.currency)}</span>
              </div>
            </section>

            <div className="dashed" />

            <section className="address">
              <div className="section-label">
                <span>Adresa za dostavu</span>
                <MapPin className="section-icon" strokeWidth={1.25} />
              </div>
              <div className="addr-body">
                <div className="addr-name">
                  {data.shippingAddress.firstName} {data.shippingAddress.lastName}
                </div>
                <div>{data.shippingAddress.address1}</div>
                {data.shippingAddress.address2 && <div>{data.shippingAddress.address2}</div>}
                <div>
                  {data.shippingAddress.postcode} {data.shippingAddress.city}
                </div>
                {data.shippingAddress.country && <div>{data.shippingAddress.country}</div>}
                {data.shippingAddress.phone && (
                  <div className="addr-phone">
                    <Phone className="phone-icon" strokeWidth={1.5} />
                    {data.shippingAddress.phone}
                  </div>
                )}
              </div>
            </section>

            <div className="dashed thick" />

            <footer className="receipt-foot">
              <div className="thanks">Hvala na povjerenju.</div>
              <div className="foot-meta">{data.shopName} · {formatDate(data.createdAt)}</div>
            </footer>
          </article>
        )}
      </div>
    </div>
  );
}

const styles = `
  .receipt-page {
    --paper: #ffffff;
    --paper-deep: #f4f4f2;
    --ink: #14130f;
    --ink-soft: #4f4d47;
    --ink-faint: #8b8980;
    --accent: #b54a2b;
    min-height: 100vh;
    background: var(--paper);
    color: var(--ink);
    font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    padding: 16px 12px 40px;
    overflow-x: hidden;
  }

  @media (min-width: 640px) {
    .receipt-page { padding: 40px 16px 60px; }
  }

  .receipt-shell {
    max-width: 480px;
    margin: 0 auto;
  }

  .receipt {
    background: #ffffff;
    border: 1px solid rgba(20, 19, 15, 0.08);
    box-shadow:
      0 1px 0 rgba(20, 19, 15, 0.04),
      0 8px 24px -12px rgba(20, 19, 15, 0.10),
      0 32px 60px -28px rgba(20, 19, 15, 0.14);
    padding: 28px 20px;
    position: relative;
    animation: rise 480ms cubic-bezier(0.2, 0.7, 0.2, 1);
  }

  @media (min-width: 640px) {
    .receipt { padding: 36px 32px; }
  }

  @keyframes rise {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .receipt-head {
    text-align: center;
    margin-bottom: 18px;
  }

  .brand {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 0.42em;
    padding-left: 0.42em;
    word-break: break-word;
  }

  .brand-logo {
    max-height: 56px;
    max-width: 70%;
    object-fit: contain;
    display: block;
    margin: 0 auto;
  }

  .brand-sub {
    margin-top: 8px;
    font-size: 11px;
    color: var(--ink-soft);
    letter-spacing: 0.18em;
    text-transform: lowercase;
  }

  .dashed {
    height: 0;
    border-top: 1.25px dashed var(--ink-faint);
    margin: 16px -4px;
    opacity: 0.7;
  }
  .dashed.thick {
    border-top-style: double;
    border-top-width: 3px;
    opacity: 0.5;
  }

  .meta {
    display: flex;
    flex-direction: column;
    gap: 6px;
    position: relative;
  }

  .meta-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    font-size: 13px;
  }

  .meta-label {
    text-transform: uppercase;
    letter-spacing: 0.18em;
    font-size: 10px;
    color: var(--ink-faint);
  }

  .meta-num {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  .meta-val {
    font-size: 13px;
    color: var(--ink-soft);
  }

  .meta-stamp-wrap {
    margin-top: 4px;
    display: flex;
    justify-content: flex-end;
  }

  /* Status pečat ima fiksnu boju (crna pozadina, bijeli tekst) — neovisan o accentu. */
  .meta-stamp {
    display: inline-block;
    color: #ffffff;
    border: 1.5px solid var(--ink);
    padding: 4px 12px 3px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.22em;
    font-weight: 700;
    transform: rotate(-3deg);
    background: var(--ink);
  }

  .section-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-transform: uppercase;
    letter-spacing: 0.22em;
    font-size: 10px;
    color: var(--ink-faint);
    margin-bottom: 14px;
  }

  .section-count {
    font-variant-numeric: tabular-nums;
    color: var(--ink-soft);
  }

  .section-icon {
    width: 14px;
    height: 14px;
    color: var(--ink-faint);
  }

  .item-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .item {
    display: grid;
    grid-template-columns: 56px 1fr auto;
    gap: 12px;
    align-items: center;
  }

  .thumb {
    width: 56px;
    height: 56px;
    background: var(--paper-deep);
    border: 1px solid rgba(28, 26, 22, 0.12);
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .thumb-fallback {
    width: 22px;
    height: 22px;
    color: var(--ink-faint);
  }

  .item-body {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .item-name {
    font-size: 13px;
    line-height: 1.35;
    color: var(--ink);
    word-break: break-word;
  }

  .item-qty {
    font-size: 11px;
    color: var(--ink-soft);
    display: inline-flex;
    align-items: baseline;
    gap: 3px;
    letter-spacing: 0.04em;
  }

  .qty-x {
    color: var(--ink-faint);
  }

  .qty-n {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }

  .item-total {
    font-size: 13px;
    font-variant-numeric: tabular-nums;
    text-align: right;
    white-space: nowrap;
  }

  .totals {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .total-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 13px;
    color: var(--ink-soft);
  }

  .total-row .num {
    font-variant-numeric: tabular-nums;
  }

  .total-row.final {
    margin-top: 4px;
    padding-top: 12px;
    border-top: 1px solid rgba(28, 26, 22, 0.18);
    color: var(--ink);
    font-size: 15px;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-weight: 700;
  }

  /* Ukupno uvijek ostaje crno — ne prati accent boju trgovine,
     jer svijetli brand tonovi znaju biti nečitljivi na bijelom. */
  .total-row.final .num {
    font-size: 22px;
    color: var(--ink);
    letter-spacing: 0.02em;
  }

  .address .addr-body {
    font-size: 13px;
    line-height: 1.55;
    color: var(--ink);
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .addr-name {
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  .addr-phone {
    margin-top: 6px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--ink-soft);
    font-variant-numeric: tabular-nums;
  }

  .phone-icon {
    width: 12px;
    height: 12px;
  }

  .receipt-foot {
    text-align: center;
    color: var(--ink-soft);
    margin-top: 4px;
  }

.thanks {
    font-size: 13px;
    color: var(--ink);
    letter-spacing: 0.04em;
  }

  .foot-meta {
    margin-top: 6px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.22em;
    color: var(--ink-faint);
  }

  /* States */
  .state-block {
    text-align: center;
    padding: 64px 20px;
    color: var(--ink-soft);
    font-size: 13px;
  }

  .state-block.error h1 {
    margin: 12px 0 6px;
    font-size: 16px;
    color: var(--ink);
    text-transform: uppercase;
    letter-spacing: 0.18em;
  }

  .state-block.error p {
    font-size: 12px;
    color: var(--ink-faint);
    letter-spacing: 0.04em;
  }

  .state-stamp {
    width: 56px;
    height: 56px;
    margin: 0 auto;
    border: 2px solid var(--accent);
    color: var(--accent);
    font-size: 28px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: rotate(-4deg);
    background: color-mix(in srgb, var(--accent) 7%, transparent);
  }

  .state-pulse {
    width: 28px;
    height: 28px;
    margin: 0 auto 14px;
    border: 1.5px solid var(--ink-faint);
    border-top-color: var(--ink);
    border-radius: 50%;
    animation: spin 900ms linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
